import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronLeft, Plus, Loader2, Trash2, Pencil, Check, ShieldAlert, UploadCloud, X, FileSpreadsheet, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  fetchProductosAdmin, subirArchivoSoftware, crearProducto, actualizarProducto,
  eliminarProducto, toggleEstadoProducto,
} from "../lib/software";

const SIMBOLO = { PEN: "S/", MXN: "$", COP: "$" };
const VACIO = {
  nombre: "", descripcion: "", categoria: "", tipo_producto: "recurso", precio: "", moneda: "PEN",
  version: "", activo: true, destacado: false, video_demo_url: "",
  archivo_path: "", archivo_nombre: "", imagen_path: "", video_demo_path: "", video_demo_nombre: "",
  licencia_path: "", licencia_nombre: "",
};

function UploadBox({ id, accept, hint, valorNombre, subiendo, progreso, onFile, onClear }) {
  return (
    <div>
      <input id={id} type="file" accept={accept} className="hidden" data-testid={id}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
      {valorNombre ? (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm">
          <span className="flex items-center gap-2 text-emerald-700 truncate"><Check size={15} /> {valorNombre}</span>
          <button type="button" onClick={onClear} className="text-slate-400 hover:text-brand-red flex-shrink-0"><X size={15} /></button>
        </div>
      ) : (
        <label htmlFor={id} className="flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-500 cursor-pointer hover:border-brand-blue hover:text-brand-blue transition-colors">
          {subiendo ? <><Loader2 size={15} className="animate-spin" /> Subiendo {progreso}%</> : <><UploadCloud size={15} /> Seleccionar archivo</>}
        </label>
      )}
      {hint && <p className="mt-1 text-[11px] text-slate-400 leading-snug">{hint}</p>}
    </div>
  );
}

export default function SoftwareAdmin() {
  const { user } = useAuth();
  const [vista, setVista] = useState("lista"); // 'lista' | 'form'
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(VACIO);
  const [productos, setProductos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [up, setUp] = useState({}); // { campo: {subiendo, progreso} }
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkDone = useRef(false);

  function cargar() {
    setCargando(true);
    fetchProductosAdmin().then(setProductos).catch(() => {}).finally(() => setCargando(false));
  }
  useEffect(() => { cargar(); }, []);

  // Deep-link desde Herramientas Técnicas (/software): ?nuevo=1 abre creación directa;
  // ?edit=<id> precarga y abre el formulario del producto. Reutiliza nuevo()/editar().
  useEffect(() => {
    if (deepLinkDone.current) return;
    const nuevoParam = searchParams.get("nuevo");
    const editParam = searchParams.get("edit");
    if (nuevoParam === "1") {
      deepLinkDone.current = true;
      nuevo();
      setSearchParams({}, { replace: true });
    } else if (editParam && productos.length) {
      const p = productos.find((x) => x.id === editParam);
      if (p) {
        deepLinkDone.current = true;
        editar(p);
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, productos]);

  const esPrograma = form.tipo_producto === "programa";
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  function nuevo() { setEditId(null); setForm(VACIO); setVista("form"); }
  function editar(p) {
    setEditId(p.id);
    setForm({
      nombre: p.nombre || "", descripcion: p.descripcion || "", categoria: p.categoria || "",
      tipo_producto: p.tipo_producto || "recurso", precio: String(p.precio ?? ""), moneda: p.moneda || "PEN",
      version: p.version || "", activo: p.activo !== false, destacado: !!p.destacado,
      video_demo_url: p.video_demo_url || "",
      archivo_path: p.archivo_path || "", archivo_nombre: p.archivo_nombre || "",
      imagen_path: p.imagen_path || "", video_demo_path: p.video_demo_path || "", video_demo_nombre: p.video_demo_nombre || "",
      licencia_path: p.licencia_path || "", licencia_nombre: p.licencia_nombre || "",
    });
    setVista("form");
  }

  async function subir(campo, file, hints = {}) {
    setUp((s) => ({ ...s, [campo]: { subiendo: true, progreso: 0 } }));
    try {
      const res = await subirArchivoSoftware(file, (pg) => setUp((s) => ({ ...s, [campo]: { subiendo: true, progreso: pg } })));
      setForm((s) => ({ ...s, ...hints(res) }));
      toast.success("Archivo subido.");
    } catch {
      toast.error("No se pudo subir el archivo (revisa el tamaño máximo).");
    } finally {
      setUp((s) => ({ ...s, [campo]: { subiendo: false, progreso: 0 } }));
    }
  }

  async function guardar(e) {
    e.preventDefault();
    if (!form.nombre.trim()) { toast.error("Ingresa el nombre del producto."); return; }
    if (form.precio === "" || isNaN(parseFloat(form.precio))) { toast.error("Ingresa un precio válido."); return; }
    if (!form.archivo_path) { toast.error("Sube el archivo descargable del producto."); return; }
    setGuardando(true);
    const payload = {
      nombre: form.nombre.trim(), descripcion: form.descripcion, categoria: form.categoria,
      tipo_producto: form.tipo_producto, precio: parseFloat(form.precio), moneda: form.moneda,
      version: esPrograma ? form.version : null,
      imagen_path: form.imagen_path || null,
      archivo_path: form.archivo_path, archivo_nombre: form.archivo_nombre,
      activo: form.activo, destacado: form.destacado,
      video_demo_path: form.video_demo_path || null, video_demo_nombre: form.video_demo_nombre || null,
      video_demo_url: form.video_demo_url || null,
      licencia_path: form.licencia_path || null, licencia_nombre: form.licencia_nombre || null,
    };
    try {
      if (editId) { await actualizarProducto(editId, payload); toast.success("Producto actualizado."); }
      else { await crearProducto(payload); toast.success("Producto creado en el catálogo."); }
      setVista("lista"); setEditId(null); setForm(VACIO); cargar();
    } catch {
      toast.error("No se pudo guardar el producto.");
    } finally { setGuardando(false); }
  }

  async function borrar(id) {
    if (!window.confirm("¿Eliminar este producto del catálogo?")) return;
    await eliminarProducto(id).catch(() => {});
    setProductos((p) => p.filter((x) => x.id !== id));
    toast.success("Producto eliminado.");
  }
  async function toggle(id) {
    const r = await toggleEstadoProducto(id).catch(() => null);
    if (r) setProductos((p) => p.map((x) => (x.id === id ? { ...x, activo: r.activo } : x)));
  }

  if (user && user.role !== "admin") {
    return (
      <div className="max-w-lg mx-auto text-center py-20" data-testid="software-admin-denegado">
        <ShieldAlert size={40} className="mx-auto text-brand-red" />
        <h2 className="mt-4 text-xl font-bold text-slate-900">Acceso restringido</h2>
        <Link to="/" className="mt-4 inline-block text-brand-blue font-semibold hover:underline">Volver al inicio</Link>
      </div>
    );
  }

  // ---------------- Vista Formulario (Imagen 04) ----------------
  if (vista === "form") {
    return (
      <div className="px-5 md:px-8 py-8 max-w-4xl mx-auto" data-testid="software-form">
        <button onClick={() => setVista("lista")} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800" data-testid="form-volver">
          <ChevronLeft size={16} /> Volver a productos
        </button>
        <h1 className="mt-3 text-2xl font-extrabold text-slate-900 tracking-tight">{editId ? "Editar producto" : "Nuevo producto"}</h1>

        <form onSubmit={guardar} className="mt-6 bg-white rounded-2xl border border-slate-100 shadow-soft p-6 md:p-8 space-y-7">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Campo label="Nombre"><input data-testid="f-nombre" value={form.nombre} onChange={(e) => set("nombre", e.target.value)} className="cl-input" placeholder="Pack de plantillas de presupuesto" /></Campo>
            <Campo label="Categoría"><input data-testid="f-cat" value={form.categoria} onChange={(e) => set("categoria", e.target.value)} className="cl-input" placeholder="plantillas-excel" /></Campo>
            <Campo label="Precio (PEN)"><input data-testid="f-precio" type="number" step="0.01" min="0" value={form.precio} onChange={(e) => set("precio", e.target.value)} className="cl-input" placeholder="0" /></Campo>
            <Campo label="Tipo">
              <select data-testid="f-tipo" value={form.tipo_producto} onChange={(e) => set("tipo_producto", e.target.value)} className="cl-input">
                <option value="recurso">Excel / PDF (Recurso digital)</option>
                <option value="programa">APK (Programa / App)</option>
              </select>
            </Campo>
          </div>

          <Campo label="Descripción"><textarea data-testid="f-desc" value={form.descripcion} onChange={(e) => set("descripcion", e.target.value)} rows={3} className="cl-input resize-none" placeholder="Describe brevemente el producto…" /></Campo>

          {esPrograma && (
            <Campo label="Versión (APK)"><input data-testid="f-version" value={form.version} onChange={(e) => set("version", e.target.value)} className="cl-input max-w-xs" placeholder="1.0.0" /></Campo>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Campo label="Archivo descargable">
              <UploadBox id="f-archivo" accept=".xlsx,.xls,.xlsm,.xlsb,.csv,.doc,.docx,.pdf,.ppt,.pptx,.zip,.rar,.apk"
                hint="Excel avanzado (.xlsx, .xls, .xlsm, .xlsb, .csv), Word, PDF, PowerPoint, ZIP, RAR o APK. Máx. 200 MB."
                valorNombre={form.archivo_nombre} subiendo={up.archivo?.subiendo} progreso={up.archivo?.progreso}
                onFile={(f) => subir("archivo", f, (r) => ({ archivo_path: r.archivo_path, archivo_nombre: r.archivo_nombre }))}
                onClear={() => setForm((s) => ({ ...s, archivo_path: "", archivo_nombre: "" }))} />
            </Campo>
            <Campo label="Miniatura (imagen, opcional)">
              <UploadBox id="f-imagen" accept="image/*" hint="JPG, PNG o WEBP. Se muestra en la tienda."
                valorNombre={form.imagen_path ? "Imagen cargada" : ""} subiendo={up.imagen?.subiendo} progreso={up.imagen?.progreso}
                onFile={(f) => subir("imagen", f, (r) => ({ imagen_path: r.archivo_path }))}
                onClear={() => setForm((s) => ({ ...s, imagen_path: "" }))} />
            </Campo>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <label className="flex items-center gap-2.5 text-sm font-semibold text-slate-700 cursor-pointer">
              <input type="checkbox" data-testid="f-activo" checked={form.activo} onChange={(e) => set("activo", e.target.checked)} className="w-4 h-4 accent-brand-blue" /> Activo (visible en la tienda)
            </label>
            <label className="flex items-center gap-2.5 text-sm font-semibold text-slate-700 cursor-pointer">
              <input type="checkbox" data-testid="f-destacado" checked={form.destacado} onChange={(e) => set("destacado", e.target.checked)} className="w-4 h-4 accent-brand-blue" /> Mostrar en destacados
            </label>
          </div>

          {/* Video demostrativo */}
          <div className="pt-5 border-t border-slate-100">
            <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-amber-600">Video demostrativo / Tutorial</p>
            <p className="mt-1 text-xs text-slate-500">Sube un video local (.mp4, .mov, .avi, .mkv, .webm) o pega un enlace de YouTube / Vimeo. Es opcional y no es vendible: solo demuestra el producto.</p>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-5">
              <Campo label="Subir video demo (opcional)">
                <UploadBox id="f-video" accept="video/*,.mp4,.mov,.avi,.mkv,.webm" hint="Máx. 200 MB."
                  valorNombre={form.video_demo_nombre} subiendo={up.video?.subiendo} progreso={up.video?.progreso}
                  onFile={(f) => subir("video", f, (r) => ({ video_demo_path: r.archivo_path, video_demo_nombre: r.archivo_nombre }))}
                  onClear={() => setForm((s) => ({ ...s, video_demo_path: "", video_demo_nombre: "" }))} />
              </Campo>
              <Campo label="O URL YouTube / Vimeo">
                <input data-testid="f-video-url" value={form.video_demo_url} onChange={(e) => set("video_demo_url", e.target.value)} className="cl-input" placeholder="https://www.youtube.com/watch?v=… o https://vimeo.com/…" />
              </Campo>
            </div>
          </div>

          {/* Archivo de contraseña / licencia */}
          <div className="pt-5 border-t border-slate-100">
            <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-amber-600">Archivo de contraseña / Licencia</p>
            <p className="mt-1 text-xs text-slate-500">Documento opcional con la contraseña de desbloqueo, instrucciones de instalación, claves de acceso o términos de licencia. Se entrega al comprador junto con el producto al aprobar el pago. Formatos permitidos: .txt, .doc, .docx, .pdf (máx. 10 MB).</p>
            <div className="mt-4 max-w-md">
              <Campo label="Archivo de contraseña / licencia (opcional)">
                <UploadBox id="f-licencia" accept=".txt,.doc,.docx,.pdf" hint=".txt, .doc, .docx, .pdf"
                  valorNombre={form.licencia_nombre} subiendo={up.licencia?.subiendo} progreso={up.licencia?.progreso}
                  onFile={(f) => subir("licencia", f, (r) => ({ licencia_path: r.archivo_path, licencia_nombre: r.archivo_nombre }))}
                  onClear={() => setForm((s) => ({ ...s, licencia_path: "", licencia_nombre: "" }))} />
              </Campo>
            </div>
          </div>

          <button type="submit" disabled={guardando} data-testid="software-guardar"
            className="inline-flex items-center gap-2 bg-brand-navy hover:bg-brand-blue disabled:opacity-50 text-white font-semibold px-6 py-3 rounded-xl transition-colors">
            {guardando ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} {editId ? "Guardar cambios" : "Crear producto"}
          </button>
        </form>
      </div>
    );
  }

  // ---------------- Vista Tabla (Imagen 03) ----------------
  return (
    <div className="px-5 md:px-8 py-8 max-w-5xl mx-auto" data-testid="software-admin">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800">
        <ChevronLeft size={16} /> Volver al Panel Admin
      </Link>

      <div className="mt-4 mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-blue">Pilar 02 · Software</p>
          <h1 className="mt-1 text-3xl font-extrabold text-slate-900 tracking-tight">Productos</h1>
          <p className="mt-1 text-slate-500 text-sm">Gestiona las plantillas y programas de la tienda. Los descuentos Pro/Premium se aplican solos.</p>
        </div>
        <button onClick={nuevo} data-testid="nuevo-producto-btn"
          className="inline-flex items-center gap-2 bg-brand-navy hover:bg-brand-blue text-white font-semibold px-5 py-2.5 rounded-xl transition-colors">
          <Plus size={17} /> Nuevo producto
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-soft overflow-hidden">
        {cargando ? (
          <div className="p-10 flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Cargando…</div>
        ) : productos.length === 0 ? (
          <div className="p-12 text-center text-slate-400" data-testid="software-vacio">
            Aún no hay productos. Crea el primero con "Nuevo producto".
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="software-tabla">
              <thead>
                <tr className="text-[11px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="text-left px-5 py-3.5">Producto</th>
                  <th className="text-left px-4 py-3.5">Categoría</th>
                  <th className="text-left px-4 py-3.5">Precio</th>
                  <th className="text-left px-4 py-3.5">Tipo</th>
                  <th className="text-left px-4 py-3.5">Estado</th>
                  <th className="text-right px-5 py-3.5">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {productos.map((p) => (
                  <tr key={p.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60" data-testid={`software-row-${p.id}`}>
                    <td className="px-5 py-4 font-semibold text-slate-900 max-w-xs">{p.nombre}{p.destacado && <span className="ml-2 text-[10px] font-bold uppercase text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Destacado</span>}</td>
                    <td className="px-4 py-4 text-slate-500">{p.categoria || "—"}</td>
                    <td className="px-4 py-4 font-bold text-slate-900 whitespace-nowrap">{SIMBOLO[p.moneda] || ""} {Number(p.precio).toFixed(2)}</td>
                    <td className="px-4 py-4">
                      <span className="inline-flex items-center gap-1.5 text-slate-500 font-medium">
                        {p.tipo_producto === "programa" ? <Smartphone size={14} /> : <FileSpreadsheet size={14} />}
                        {p.tipo_producto === "programa" ? "APK" : "Excel/PDF"}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <button onClick={() => toggle(p.id)} data-testid={`software-toggle-${p.id}`}
                        className={`text-xs font-bold uppercase tracking-wide ${p.activo ? "text-emerald-600" : "text-slate-400"}`}>
                        {p.activo ? "Activo" : "Oculto"}
                      </button>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => editar(p)} title="Editar" data-testid={`software-editar-${p.id}`} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-brand-blue">
                          <Pencil size={16} />
                        </button>
                        <button onClick={() => borrar(p.id)} title="Eliminar" data-testid={`software-borrar-${p.id}`} className="p-2 rounded-lg text-brand-red hover:bg-red-50">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const Campo = ({ label, children }) => (
  <label className="block">
    <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);
